<template>
  <ol class="answerList">
    <li v-for="(answer, index) in answers"
        :class="{'size size--half': answer.isHalf, 'judgeCorrect':answer.isCorrectAnswer && !answer.isDeleted,
        'judgeWrong': !answer.isDeleted && !answer.isCorrectAnswer, 'math-field': isKeyboard}">
      <div v-if="isKeyboard && answer.label.length > 0" class="number" v-html="labelText(answer)"></div>
      <span v-else-if="answer.label" class="number">{{answer.label}}</span>
      <div v-if="answer.prefix || answer.suffix"
           :class="{'stringBox':true,'stringBox--lines':answer.isMulti,
           'stringBox--mathFieldWidth': isKeyboard && answer.label.length > 0,
           'stringBox--oneLine': isKeyboard?prefixSuffixDisplay(index):false,
           'disabled-input': answer.isDeleted && answer.text.length == 0}">
        <div class="stringBox__string" v-if="isVertical">{{answer.prefix}}</div>
        <div class="stringBox__string" v-else-if="isKeyboard && answer.prefix.length > 0" v-html="prefixText(answer)"></div>
        <div class="stringBox__string" v-else-if="answer.prefix.length > 0">{{answer.prefix}}</div>
        <div v-if="!answer.isCorrectAnswer && isEnglish && answer.isInput && autoGradingPage"
          class="answerTextarea answerTextarea--answerEnglishFieldOverflow">
          <div :ref="(el) => {answerRef[index] = el}">
            {{showDiff(answer.text, answer.answers, index)}}
          </div>
        </div>
        <div v-else :ref="(el) => {answerTextareaRef[index] = el}"
           :class="{'answerTextarea':true, 'answerPageNoAnswer': answer.isNoAnswer,
           'answerTextarea--answerMathFieldWidth': isKeyboard && answer.text.length > 0}">
          <markdown-view :rawText="answerText(answer.text, answer.isInput)" :souruby="isSouruby"></markdown-view>
        </div>
        <div class="stringBox__string" v-if="isVertical">{{answer.suffix}}</div>
        <div class="stringBox__string" v-else-if="isKeyboard && answer.suffix.length > 0" v-html="suffixText(answer)"></div>
        <div class="stringBox__string" v-else-if="answer.suffix.length > 0">{{answer.suffix}}</div>
      </div>
      <template v-else>
        <div v-if="!answer.isCorrectAnswer && isEnglish && answer.isInput && autoGradingPage"
           class="answerTextarea" :class="{'disabled-input': answer.isDeleted && answer.text.length == 0}"
        >
          <div :ref="(el) => {answerRef[index] = el}">
            {{showDiff(answer.text, answer.answers, index)}}
          </div>
        </div>
        <div class="answerTextarea" v-else
           :class="{'answerPageNoAnswer': answer.isNoAnswer, 'disabled-input': answer.isDeleted && answer.text.length == 0}">
          <markdown-view :rawText="answerText(answer.text, answer.isInput)" :souruby="isSouruby"></markdown-view>
        </div>
      </template>
    </li>
  </ol>
</template>

<script setup lang="ts">
  import {ref, nextTick} from "vue";
  import { diffWords } from "diff"
  import { markdown as md } from "@/util/markdown";

  interface IAnswerGrading{
    text: string;
    isCorrectAnswer: boolean;
    isHalf: boolean;
    isMulti: boolean;
    prefix: string;
    suffix: string;
    label: string;
    answers?: Array<any>;
    isInput: boolean;
    isNoAnswer: boolean;
    isDeleted: boolean;  // track quiz_history is deleted for hide OX sign in answer column
  }
  const answerTextareaRef = ref([])
  const answerRef = ref([])
  const props = withDefaults(
    defineProps<{
      answers?: IAnswerGrading[];
      autoGradingPage?: boolean;
      isSouruby?: boolean;
      isEnglish?: boolean;
      isVertical?: boolean;
      isKeyboard?: boolean;
      isScience?: boolean;
    }>(),
    {
      answers: () => [],
      autoGradingPage: false,
      isSouruby: false,
      isEnglish: false,
      isVertical: false,
      isKeyboard: false,
      isScience: false,
    }
  );

  const labelText = (answer: IAnswerGrading) => {
    return md.render(answer.label);
  }

  const prefixText = (answer: IAnswerGrading) => {
    return md.render(answer.prefix);
  }

  const suffixText = (answer: IAnswerGrading) => {
    return md.render(answer.suffix);
  }

  function answerText(text:string, isInput: boolean) {
    // NFR-4924 解答欄中の式の表示が答え合わせの前後で異なる②. isInput prevent to change original answer if answer format other than input
    // NFR-3670 理科で下付き、上付き文字がある場合\ceコマンドを付けて表示する
    if(props.isScience && text.length > 0 && isInput){
      let isSubSup =(/\_|\^/g).test(text)
      // add chemical equeation \\ce{fourmula} for rendering sub and superscript
      if(isSubSup){
        text = text.replace(/(\$)(.*?)(\$)/g, '$\\ce{$2}$')
      }
    }
    return text
  }

  // NFR-3049 プレフィックスサフィックスありmathfieldではみ出す場合の表示のため
  function prefixSuffixDisplay(index) {
    nextTick(() => {
      let ansText: any = answerTextareaRef.value[index]
      if (ansText) {
        if (ansText.clientWidth <= ansText.childNodes[0].clientWidth) {
          let parent: any = ansText.parentNode
          parent.classList.remove("stringBox--oneLine")
          return false
        }
      }
    })
    return true
  }
  function showDiff(inputText, answers, index){
    nextTick(() => {
      let cls = '',
        span = null,
        display:any = answerRef.value[index],
        fragment = document.createDocumentFragment();
      display.innerHTML = "";
      if(inputText.trim()==''){
        return
      }
      else if(answers.length == 0){
        return inputText
      }
      else {
        let min_len = Math.min.apply(Math, answers.map(function(str) { return str.split(' ').length; }));
        if(min_len==1 && inputText.split(' ').length == 1){
          display.appendChild(document.createTextNode(inputText));
        }else{
          let diffs:Array<any> = [];
          for(let answer of answers){
            diffs.push(diffWords(inputText, answer))
          }
          let tgt = diffs.reduce((diff1, diff2) => diff1.length <= diff2.length ? diff1 : diff2);
          tgt.forEach(function(part){
            cls = part.added ? 'added' :
              part.removed ? 'removed' : 'n';
            span = document.createElement('span');
            span.classList.add(cls);
            span.appendChild(document.createTextNode(part.value));
            fragment.appendChild(span);
          });
          display.appendChild(fragment);
        }
      }
    })
    return ''
  }
</script>
